/**
 * Canonical catalog of filterable facilities. Keys are the camelCase keys of
 * LocationFacilitySchema or leaves of UnitFacilitySchema's sections
 * (src/api/accommodation/types.ts) and are passed straight to the /offer
 * endpoint's `facility` query param (comma-separated, AND logic). Groups are
 * ordered by user demand (wellness first), not by the API shape.
 */

export interface FacilityOption {
  key: string
  label: string
}

export interface FacilityGroup {
  label: string
  options: FacilityOption[]
}

export const FACILITY_GROUPS: FacilityGroup[] = [
  {
    label: 'Wellness a relax',
    options: [
      { key: 'sauna', label: 'Sauna' },
      { key: 'whirlpool', label: 'Vířivka' },
      { key: 'hotTub', label: 'Koupací sud' },
      { key: 'pool', label: 'Bazén' },
      { key: 'fitness', label: 'Fitness' },
    ],
  },
  {
    label: 'Interiér',
    options: [
      { key: 'indoorFireplace', label: 'Krb' },
      { key: 'woodHeating', label: 'Kamna na dřevo' },
      { key: 'airConditioning', label: 'Klimatizace' },
      { key: 'internet', label: 'Internet' },
      { key: 'tv', label: 'TV' },
      { key: 'washingMachine', label: 'Pračka' },
    ],
  },
  {
    label: 'Venku',
    options: [
      { key: 'garden', label: 'Zahrada' },
      { key: 'terrace', label: 'Terasa' },
      { key: 'grill', label: 'Gril' },
      { key: 'outdoorFireplace', label: 'Ohniště' },
      { key: 'freeParking', label: 'Parkování zdarma' },
      { key: 'sportFacility', label: 'Sportoviště' },
    ],
  },
  {
    label: 'Pro děti',
    options: [
      { key: 'childCrib', label: 'Dětská postýlka' },
      { key: 'highChair', label: 'Dětská židlička' },
      { key: 'childrenPlayground', label: 'Dětské hřiště' },
    ],
  },
  {
    label: 'Kuchyně',
    options: [
      { key: 'dishwasher', label: 'Myčka' },
      { key: 'oven', label: 'Trouba' },
      { key: 'stove', label: 'Sporák' },
      { key: 'microwave', label: 'Mikrovlnná trouba' },
      { key: 'refrigerator', label: 'Lednice' },
      { key: 'dishes', label: 'Nádobí' },
      { key: 'basicCookingIngredients', label: 'Základní ingredience' },
      { key: 'automaticCoffeeMachine', label: 'Kávovar' },
      { key: 'nespressoCoffeeMachine', label: 'Nespresso kávovar' },
      { key: 'filterCoffeeMaker', label: 'Filtrovaná káva' },
    ],
  },
  {
    label: 'Koupelna',
    options: [
      { key: 'bathtub', label: 'Vana' },
      { key: 'shower', label: 'Sprchový kout' },
      { key: 'towels', label: 'Ručníky' },
      { key: 'toiletries', label: 'Mýdla a šampony' },
      { key: 'hairdryer', label: 'Fén' },
    ],
  },
  {
    label: 'Ložnice',
    options: [
      { key: 'doubleBed', label: 'Manželská postel' },
      { key: 'twinBeds', label: 'Oddělené postele' },
    ],
  },
  {
    label: 'Jídlo a služby',
    options: [
      { key: 'breakfastIncluded', label: 'Snídaně v ceně' },
      { key: 'restaurant', label: 'Restaurace' },
      { key: 'bar', label: 'Bar' },
    ],
  },
]

export const FACILITY_LABELS: Record<string, string> = Object.fromEntries(
  FACILITY_GROUPS.flatMap((group) => group.options.map((option) => [option.key, option.label])),
)

/** Parses the comma-separated URL/API param, dropping unknown keys. */
export function parseFacilityKeys(facility: string | undefined): string[] {
  if (!facility) {
    return []
  }
  return facility
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key in FACILITY_LABELS)
}

export function serializeFacilityKeys(keys: string[]): string | undefined {
  return keys.length > 0 ? keys.join(',') : undefined
}

/** Short human summary for triggers: "Nezáleží", "Sauna", "Sauna +2". */
export function formatFacilitySummary(keys: string[]): string {
  const [firstKey] = keys
  if (firstKey === undefined) {
    return 'Nezáleží'
  }
  const first = FACILITY_LABELS[firstKey] ?? firstKey
  return keys.length === 1 ? first : `${first} +${keys.length - 1}`
}
